import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { ChartColumn, Plus, TrendingDown, TrendingUp } from 'lucide-react'
import { Bar, BarChart, LabelList, XAxis, YAxis } from 'recharts'

import { priorPeriodLabel } from './filters/dateRange'
import type { DateFilter } from './filters/types'
import EmptyCard from './EmptyCard'

import type { DesktopAppRow, DesktopAppsResponse } from '@/api/teamInsights'
import { Button } from '@/components/ui/button'
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart'
import { Skeleton } from '@/components/ui/skeleton'
import { cn, formatTooltipNumber } from '@/lib/utils'

const BAR_COLOR = 'var(--chart-1)'
const ROW_LIMIT = 10
const ROW_HEIGHT = 28
const MAX_LABEL_CHARS = 14
const Y_AXIS_WIDTH = 120
// Rendering thousands of recharts bars on the same frame as the click can stall the main thread
const EXPAND_SKELETON_THRESHOLD = 250
const EXPAND_SKELETON_DELAY_MS = 1

const formatWordCount = (n: number): string => formatTooltipNumber(n)

const formatPct = (pct: number): string => {
  if (pct >= 1) return `${pct.toFixed(0)}%`
  if (pct >= 0.1) return `${pct.toFixed(1)}%`
  return '<0.1%'
}

const chartConfig = {
  pct_of_total: { label: 'Share', color: BAR_COLOR },
} satisfies ChartConfig

interface DesktopAppsChartProps {
  response: DesktopAppsResponse
  dateFilter: DateFilter
  lockedPreview?: boolean
  // Member detail reuses this chart for one user; the team-level "invite team
  // members" empty state doesn't apply, so we drop the description and CTA.
  singleMember?: boolean
}

const DesktopAppsChart = ({ response, dateFilter, lockedPreview = false, singleMember }: DesktopAppsChartProps) => {
  const [expanded, setExpanded] = React.useState(false)
  const [isExpanding, setIsExpanding] = React.useState(false)
  const navigate = useNavigate()

  React.useEffect(() => {
    if (!isExpanding) return
    const id = window.setTimeout(() => {
      setExpanded(true)
      setIsExpanding(false)
    }, EXPAND_SKELETON_DELAY_MS)
    return () => window.clearTimeout(id)
  }, [isExpanding])

  const sortedRows = React.useMemo<DesktopAppRow[]>(
    () => [...response.rows].sort((a, b) => b.word_count - a.word_count),
    [response.rows]
  )

  if (response.distinct_app_count === 0 || sortedRows.length === 0) {
    return (
      <EmptyCard
        icon={<ChartColumn className="size-5" />}
        title="No desktop apps used"
        description={singleMember ? undefined : 'Add team members to begin seeing more usage trends.'}
        cta={
          singleMember
            ? undefined
            : {
                label: 'Invite team members',
                icon: <Plus className="size-4" />,
                onClick: () => navigate('/app/team'),
              }
        }
      />
    )
  }

  const rows = expanded ? sortedRows : sortedRows.slice(0, ROW_LIMIT)
  const canExpand = sortedRows.length > ROW_LIMIT

  // pct_change is in percent units (2 dp). When the filter includes today
  // (or has no end_date), backend compares trailing 7d vs prior 7d; when
  // the filter excludes today, it compares the caller's range to the
  // equivalent prior duration. Null only when prior=0 with non-zero
  // current (mathematically undefined).
  const pctChange = response.distinct_app_count_pct_change_vs_prev_period ?? null
  const isPositive = pctChange !== null && pctChange > 0
  const isNegative = pctChange !== null && pctChange < 0
  const TrendIcon = isPositive ? TrendingUp : isNegative ? TrendingDown : null
  const pctChangeLabel = priorPeriodLabel(dateFilter)

  return (
    <Card className="gap-0 py-0">
      <CardHeader className="flex items-center gap-4 space-y-0 border-b pl-6 pr-3 !py-3 sm:flex-row">
        <div className="flex flex-1 items-center gap-4">
          <CardTitle className="text-xl font-semibold">
            {lockedPreview ? (
              'Desktop apps used'
            ) : (
              <>
                {response.distinct_app_count.toLocaleString()} desktop{' '}
                {response.distinct_app_count === 1 ? 'app' : 'apps'} used
              </>
            )}
          </CardTitle>
          {pctChange !== null && (
            <div
              className={cn(
                'flex items-center gap-1 text-[15px] font-semibold leading-5',
                isPositive ? 'text-[var(--green-600)]' : 'text-muted-foreground'
              )}
            >
              {TrendIcon && <TrendIcon className="size-5" />}
              <span>
                {Math.abs(pctChange)}% {pctChangeLabel}
              </span>
            </div>
          )}
        </div>
        {canExpand && (
          <CardAction className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (expanded) {
                  setExpanded(false)
                  return
                }
                if (sortedRows.length > EXPAND_SKELETON_THRESHOLD) {
                  setIsExpanding(true)
                  return
                }
                setExpanded(true)
              }}
              className="text-[15px] leading-5"
            >
              {expanded ? 'Show less' : 'See all'}
            </Button>
          </CardAction>
        )}
      </CardHeader>
      <CardContent className={cn('px-6 py-4', expanded && 'max-h-[480px] overflow-y-auto')}>
        {isExpanding ? (
          <Skeleton className="w-full" style={{ height: Math.min(sortedRows.length * ROW_HEIGHT, 448) }} />
        ) : (
          <ChartContainer config={chartConfig} className="w-full" style={{ height: rows.length * ROW_HEIGHT }}>
            <BarChart
              accessibilityLayer
              data={rows}
              layout="vertical"
              margin={{ top: 0, right: 32, bottom: 0, left: 4 }}
              barCategoryGap={6}
            >
              <XAxis type="number" hide domain={[0, 'dataMax']} />
              <YAxis
                type="category"
                dataKey="app_key"
                width={Y_AXIS_WIDTH}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value: string) =>
                  value.length > MAX_LABEL_CHARS ? `${value.slice(0, MAX_LABEL_CHARS - 1)}…` : value
                }
                tick={{
                  fontSize: 15,
                  fontWeight: 600,
                  fill: 'var(--foreground)',
                  textAnchor: 'start',
                  dx: -Y_AXIS_WIDTH + 4,
                }}
              />
              <ChartTooltip
                cursor={false}
                content={
                  <ChartTooltipContent
                    hideLabel
                    formatter={(_value, _name, item) => {
                      const row = item.payload as unknown as DesktopAppRow
                      return (
                        <div className="flex items-center gap-2">
                          <span className="inline-block size-3 rounded-[3px]" style={{ background: BAR_COLOR }} />
                          <span className="text-[13px] font-medium text-muted-foreground">{row.app_key}</span>
                          <span className="text-[13px] font-semibold text-foreground">
                            {formatWordCount(row.word_count)} words
                          </span>
                        </div>
                      )
                    }}
                  />
                }
              />
              <Bar
                dataKey="pct_of_total"
                fill={BAR_COLOR}
                radius={10}
                barSize={8}
                minPointSize={2}
                isAnimationActive={false}
              >
                <LabelList
                  dataKey="pct_of_total"
                  position="right"
                  formatter={(value) => formatPct(typeof value === 'number' ? value : 0)}
                  className="fill-foreground"
                  fontSize={12}
                />
              </Bar>
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  )
}

export default DesktopAppsChart
